// ----------------------------------------------------------------------------
// Section: Featured Collection
// ----------------------------------------------------------------------------
//
// This is a section that displays featured collection items and optional button
//
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------
import Swiper, { Navigation, Pagination, A11y, Keyboard } from 'swiper';
import 'snippets/card/card';
import 'snippets/icon/icon';

// Style includes
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';
import 'swiper/css/a11y';
import 'swiper/css/keyboard';
import './_featured-collection.scss';

// -------------------------------------
// Variables
// -------------------------------------

// Selectors
const FEATURED_COLLECTION_SELECTOR = '.featured-collection';
const CAROUSEL_WRAPPER_SELECTOR = '.collection-carousel__wrapper';
const CAROUSEL_SELECTOR = '.carousel--featured-collection';
const SWIPER_WRAPPER_SELECTOR = '.swiper-wrapper';
const SWIPER_SLIDE_SELECTOR = '.swiper-slide';
const SWIPER_ARROW_SELECTOR = '.swiper-arrow';
const PREV_BUTTON_SELECTOR = '.swiper-button-prev';
const NEXT_BUTTON_SELECTOR = '.swiper-button-next';
const SWIPER_PAGINATION_SELECTOR = '.swiper-pagination';
const SWIPER_GOOEY_SELECTOR = '.gooey-container';

// Classes
const NO_SLIDER_INIT_CLASS = 'no-slider';
const GRID_INIT_CLASS = 'grid-container';
const ARROW_INIT_CLASS = 'swiper-arrow-initialized';

// Other
const SLIDES_PER_VIEW_MOBILE = 2;
const SLIDES_PER_VIEW_TABLET = 3;
const SLIDES_PER_VIEW_DESKTOP = 4;
const TABLET_MEDIA_QUERY = '(min-width: 680px)';
const DESKTOP_MEDIA_QUERY = '(min-width: 1024px)';


// -------------------------------------
// Helpers
// -------------------------------------

const initFeaturedCollection = (collectionList) => {
    const carousel = collectionList.querySelector(CAROUSEL_SELECTOR);
    const carouselWrapper = carousel.querySelector(SWIPER_WRAPPER_SELECTOR);
    const slides = carousel.querySelectorAll(SWIPER_SLIDE_SELECTOR);
    const slidesPerView = getSlidesPerView();

    if (slides.length > slidesPerView) {
        carouselWrapper.classList.remove(GRID_INIT_CLASS);
        collectionList.classList.remove(NO_SLIDER_INIT_CLASS);
        initFeaturedCollectionSwiper(collectionList);
    } else {
        carouselWrapper.classList.add(GRID_INIT_CLASS);
        collectionList.classList.add(NO_SLIDER_INIT_CLASS);
    }
};

const initFeaturedCollectionSwiper = (carousel) => {
    const carouselWrapper = carousel.querySelector(CAROUSEL_WRAPPER_SELECTOR);
    const sectionID = carousel.dataset.sectionId;
    const parentSelector = FEATURED_COLLECTION_SELECTOR + '--' + sectionID;
    const swiperArrows = carouselWrapper.querySelectorAll(SWIPER_ARROW_SELECTOR);
    const gooeyAnimationContainer = carouselWrapper.querySelector(SWIPER_GOOEY_SELECTOR);

    if (gooeyAnimationContainer) {
        gooeyAnimationContainer.style.display = 'flex';
    }

    addClassToElements(swiperArrows, ARROW_INIT_CLASS);

    const swiper = new Swiper(parentSelector + ' ' + CAROUSEL_SELECTOR, {
        modules: [Navigation, Pagination, A11y, Keyboard],
        slidesPerView: SLIDES_PER_VIEW_MOBILE,
        spaceBetween: 25,
        observer: true,
        observeParents: true,
        a11y: true,
        watchOverflow: true,
        breakpoints: {
            680: {
                slidesPerView: SLIDES_PER_VIEW_TABLET
            },
            1024: {
                slidesPerView: SLIDES_PER_VIEW_DESKTOP
            }
        },
        navigation: {
            nextEl: parentSelector + ' ' + NEXT_BUTTON_SELECTOR,
            prevEl: parentSelector + ' ' + PREV_BUTTON_SELECTOR
        },
        pagination: {
            el: parentSelector + ' ' + SWIPER_PAGINATION_SELECTOR,
            type: 'bullets',
            clickable: true
        },
        on: {
            breakpoint: function (swiper) {
                const activeIndex  = swiper.activeIndex + 1;
                const activeBullet = carouselWrapper.querySelector(`.swiper-pagination-bullet:nth-child(${activeIndex})`);
                const gooeySlider  = carouselWrapper.querySelector('.gooey-slider');

                if (activeBullet !== null && gooeySlider !== null) {
                    const offsetLeft = (offsetLeft == 0) ? activeBullet.offsetLeft + 10 : activeBullet.offsetLeft + 13;

                    gooeySlider.style.left = `${offsetLeft}px`;
                }
            },
            beforeTransitionStart: function (swiper) {
                const previousIndex = swiper.previousIndex;
                const activeIndex   = swiper.activeIndex;
                const bulletIndex   = swiper.activeIndex + 1;
                const nextBullet    = carouselWrapper.querySelector(`.swiper-pagination-bullet:nth-child(${bulletIndex})`);
                const gooeySlider   = carouselWrapper.querySelector('.gooey-slider');

                if (nextBullet !== null && gooeySlider !== null) {
                    const offsetLeft = (previousIndex < activeIndex) ? nextBullet.offsetLeft : nextBullet.offsetLeft;

                    gooeySlider.style.left = `${offsetLeft}px`;
                }
            },
        }
    });

    swiper.slides.forEach((slide, index) => {
        const slideLink = slide.querySelector('a');

        if (slideLink) {
            slideLink.addEventListener('focus', (e) => {
                swiper.slideTo(index);
            });
        }
    });
};

const getSlidesPerView = () => {
    const desktopMedia = window.matchMedia(DESKTOP_MEDIA_QUERY);
    const tabletMedia = window.matchMedia(TABLET_MEDIA_QUERY);

    if (desktopMedia.matches) {
        return SLIDES_PER_VIEW_DESKTOP;
    } else if (tabletMedia.matches) {
        return SLIDES_PER_VIEW_TABLET;
    } else {
        return SLIDES_PER_VIEW_MOBILE;
    }
};

const addClassToElements = (elems, className) => {
    elems.forEach((elem) => {
        elem.classList.add(className);
    });
};

// -------------------------------------
// Hooks
// -------------------------------------

document.addEventListener('DOMContentLoaded', () => {
    const collectionLists = document.querySelectorAll(FEATURED_COLLECTION_SELECTOR);

    if (collectionLists != null) {
        collectionLists.forEach((collectionList) => {
            initFeaturedCollection(collectionList);
        });
    }
});
